import { Card } from '../../../molecules/Card/Card';
import { isEmptyTotal, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import type { ChartSeriesToken } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { DonutChart } from '../../../organisms/DonutChart/DonutChart';
import { token } from '../../../tokens/tokens';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type SeededVsOrganicView = {
  /** `Organic volume` and `Seeded volume`. */
  kpis: KPIMetric[];
  /** One slice per origin present, in roster order; the value is on `mentions`. */
  rows: ChartRow[];
  nameKey: string;
  /** One colour per slice, from the origin's roster position. */
  colors: ChartSeriesToken[];
  legend: { label: string; color: ChartSeriesToken }[];
  /** Organic's share of the split, already rounded — `79%`. */
  centerValue: string;
  formatValue: (value: number) => string;
};

export type SeededVsOrganicCardProps = WidgetBlockProps & {
  view?: SeededVsOrganicView;
  /** Opens the conversations behind a slice (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `849:29599` — the donut splitting placed conversation from organic.
 *
 * Organic, not seeded — and the ring reads the same way round as the figures
 * beside it. Until 2026-09-23 the card put `Organic volume` above a ring
 * centred on `seeded 21%`: one figure of the pair, and the *other* half of
 * the split in the middle, so the two numbers on the card were about
 * different things and neither said what the big blue arc was.
 *
 * Both volumes are KPIs now and the centre is organic's share, so the card
 * reads one way throughout: how much of this conversation we did not place.
 */
export function SeededVsOrganicCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: SeededVsOrganicCardProps) {
  const hasData =
    view !== undefined && view.rows.length > 0 && !isEmptyTotal(view.rows, 'mentions');
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Seeded vs organic split"
      helpText="Separates paid or seeded conversation from organic, so reach is not mistaken for advocacy."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <DonutChart
            data={view.rows}
            onViewDetails={onViewDetails}
            nameKey={view.nameKey}
            valueKey="mentions"
            height={PLOT_HEIGHT}
            legend="none"
            centerLabel="organic"
            centerValue={view.centerValue}
            colors={view.colors}
            formatValue={view.formatValue}
          />
        )
      }
      legend={
        view && (
          <ChartLegend
            items={view.legend.map((item) => ({ label: item.label, color: token[item.color] }))}
          />
        )
      }
    />
  );
}
